import { ApiError } from '@gopinhos/shared/api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCreateLead } from '../hooks/use-leads';

export function LeadCreatePage(): JSX.Element {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const create = useCreateLead(projectId ?? '');
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="mx-auto max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle>{t('leadCreate.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={async (e) => {
              e.preventDefault();
              setError(null);
              const fd = new FormData(e.currentTarget);
              const value = fd.get('valueCents');
              try {
                const result = (await create.mutateAsync({
                  title: String(fd.get('title') ?? '').trim(),
                  contactName: emptyToUndef(String(fd.get('contactName') ?? '')),
                  contactEmail: emptyToUndef(String(fd.get('contactEmail') ?? '')),
                  contactPhone: emptyToUndef(String(fd.get('contactPhone') ?? '')),
                  company: emptyToUndef(String(fd.get('company') ?? '')),
                  source: emptyToUndef(String(fd.get('source') ?? '')),
                  valueCents: value ? Number(value) * 100 : undefined,
                  currency: emptyToUndef(String(fd.get('currency') ?? '')),
                  customFields: {},
                })) as { id: string };
                navigate(`/leads/${result.id}`);
              } catch (err) {
                setError(err instanceof ApiError ? err.problem.title : t('leadCreate.errorGeneric'));
              }
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="title">{t('leadCreate.titleField')}</Label>
              <Input id="title" name="title" required maxLength={200} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="contactName">{t('leadCreate.contactName')}</Label>
                <Input id="contactName" name="contactName" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="company">{t('leadCreate.company')}</Label>
                <Input id="company" name="company" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="contactEmail">{t('leadCreate.contactEmail')}</Label>
                <Input id="contactEmail" type="email" name="contactEmail" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="contactPhone">{t('leadCreate.contactPhone')}</Label>
                <Input id="contactPhone" name="contactPhone" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="valueCents">{t('leadCreate.valueWhole')}</Label>
                <Input id="valueCents" name="valueCents" type="number" min={0} step={1} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="currency">{t('leadCreate.currency')}</Label>
                <Input id="currency" name="currency" maxLength={3} placeholder="USD" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="source">{t('leadCreate.source')}</Label>
              <Input id="source" name="source" placeholder={t('leadCreate.sourcePlaceholder')} />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" type="button" onClick={() => navigate(-1)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? t('common.creating') : t('leadCreate.create')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function emptyToUndef(s: string): string | undefined {
  const t = s.trim();
  return t ? t : undefined;
}
